/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */

import { AppToggle } from "./app-toggle.js";

interface ToggleOption { value: string; label: string; disabled?: boolean }

const styles = css`
  :host { display: inline-flex; }
  .group { align-items: center; display: flex; width: fit-content; }
  app-toggle { flex: 0 0 auto; }
  :host([variant="outline"]) app-toggle + app-toggle { margin-left: -1px; }
`;

export class AppToggleGroup extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppToggleGroup)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["options","data-options","value","data-value","kind","data-kind","variant","data-variant","size","data-size"];

  #options = [];
  #value = [];
  #kind = "multiple";
  #variant = "default";
  #size = "default";
  #optionsSet = false;
  #valueSet = false;
  #kindSet = false;
  #variantSet = false;
  #sizeSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get options() {
    return this.#options;
  }

  set options(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#options, next)) return;
    this.#options = next;
    this.#optionsSet = true;
    this.#updateBindings('options');
  }

  get value() {
    return this.#value;
  }

  set value(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#value, next)) return;
    this.#value = next;
    this.#valueSet = true;
    this.#updateBindings('value');
  }

  get kind() {
    return this.#kind;
  }

  set kind(value) {
    const raw = value ?? "multiple";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#kind, next)) return;
    this.#kind = next;
    this.#kindSet = true;
    this.#updateBindings('kind');
  }

  get variant() {
    return this.#variant;
  }

  set variant(value) {
    const raw = value ?? "default";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#variant, next)) return;
    this.#variant = next;
    this.#variantSet = true;
    this.#updateBindings('variant');
  }

  get size() {
    return this.#size;
  }

  set size(value) {
    const raw = value ?? "default";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#size, next)) return;
    this.#size = next;
    this.#sizeSet = true;
    this.#updateBindings('size');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-options')) this.options = this.dataset.options;
    if (this.hasAttribute('data-value')) this.value = this.dataset.value;
    if (this.hasAttribute('data-kind')) this.kind = this.dataset.kind;
    if (this.hasAttribute('data-variant')) this.variant = this.dataset.variant;
    if (this.hasAttribute('data-size')) this.size = this.dataset.size;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.options = props.options;
      this.value = props.value;
      this.kind = props.kind;
      this.variant = props.variant;
      this.size = props.size;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#optionsSet) this.options = this.dataset.options;
    if (!this.#valueSet) this.value = this.dataset.value;
    if (!this.#kindSet) this.kind = this.dataset.kind;
    if (!this.#variantSet) this.variant = this.dataset.variant;
    if (!this.#sizeSet) this.size = this.dataset.size;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'options':
      case 'data-options':
        this.options = newValue;
        break;
      case 'value':
      case 'data-value':
        this.value = newValue;
        break;
      case 'kind':
      case 'data-kind':
        this.kind = newValue;
        break;
      case 'variant':
      case 'data-variant':
        this.variant = newValue;
        break;
      case 'size':
      case 'data-size':
        this.size = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const buttons = this.#options.map((option, index) => `<app-toggle data-value="${this.#escAttr(option.value)}" data-pressed="${this.#attrEsc(this.#value.includes(option.value))}" data-disabled="${this.#attrEsc(option.disabled ?? false)}" variant="${this.#escAttr(this.#variant)}" size="${this.#escAttr(this.#size)}" position="${this.#escAttr((this.#options.length === 1) ? "only" : ((index === 0) ? "first" : ((index === (this.#options.length - 1)) ? "last" : "middle")))}">${""}${this.#escText(option.label)}</app-toggle>`).join("");
    return `<style>${this.#escText(styles)}</style><div class="group" role="group" data-argon="1233"><!--argon-text:0-->${buttons}<!--/argon-text:0--></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const buttons = (prop === undefined || prop === 'options' || prop === 'value' || prop === 'variant' || prop === 'size') ? (this.#options.map((option, index) => `<app-toggle data-value="${this.#escAttr(option.value)}" data-pressed="${this.#attrEsc(this.#value.includes(option.value))}" data-disabled="${this.#attrEsc(option.disabled ?? false)}" variant="${this.#escAttr(this.#variant)}" size="${this.#escAttr(this.#size)}" position="${this.#escAttr((this.#options.length === 1) ? "only" : ((index === 0) ? "first" : ((index === (this.#options.length - 1)) ? "last" : "middle")))}">${""}${this.#escText(option.label)}</app-toggle>`).join("")) : undefined;
    if (prop === undefined || prop === 'options' || prop === 'value' || prop === 'variant' || prop === 'size') {
      const __argonNext = `${buttons}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '1233').forEach(el => this.#listen(el, '1233', 'pressed-change', (event: CustomEvent) => { const target = event.target as HTMLElement; const next = target.dataset.value ?? ""; const values = this.#kind === "single" ? (this.#value.includes(next) ? [] : [next]) : (this.#value.includes(next) ? this.#value.filter((item) => item !== next) : [...this.#value, next]); this.dispatchEvent(new CustomEvent("value-change", { bubbles: true, composed: true, detail: { value: values } })); }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
